import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PERM, hasGlobalPermission } from "@/lib/permissions";

const DevFieldNotesContext = createContext(null);

/**
 * Dev-only scratch notes attached to the "<>" feed-path tooltips (system.dev_tools
 * only) — a temporary aid for finalizing which upstream fields to keep, not
 * part of the product data model. Fetched once per session for whoever can
 * see the tooltips at all.
 */
export function DevFieldNotesProvider({ children }) {
  const { user } = useAuth();
  const isSystemAdmin = hasGlobalPermission(user, PERM.SYSTEM_DEV_TOOLS);
  const [notesByKey, setNotesByKey] = useState({});

  const refresh = useCallback(async () => {
    if (!isSystemAdmin) return;
    try {
      const { data } = await client.get("/dev-field-notes");
      setNotesByKey(data || {});
    } catch {
      // Best-effort — tooltips still work without notes.
    }
  }, [isSystemAdmin]);

  useEffect(() => {
    if (isSystemAdmin) refresh();
    else setNotesByKey({});
  }, [isSystemAdmin, refresh]);

  const saveNote = useCallback(async (fieldKey, note) => {
    const { data } = await client.put("/dev-field-notes", { field_key: fieldKey, note });
    setNotesByKey((prev) => {
      const next = { ...prev };
      if (data.note) next[fieldKey] = data;
      else delete next[fieldKey];
      return next;
    });
    return data;
  }, []);

  const value = useMemo(() => ({ notesByKey, saveNote, refresh }), [notesByKey, saveNote, refresh]);

  return <DevFieldNotesContext.Provider value={value}>{children}</DevFieldNotesContext.Provider>;
}

export function useDevFieldNotes() {
  const ctx = useContext(DevFieldNotesContext);
  if (!ctx) throw new Error("useDevFieldNotes must be used within DevFieldNotesProvider");
  return ctx;
}
